import React, { useState } from "react";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import {
  AssetType,
  useAddChildrenToParentAssetMutation,
  useAddParentsToChildAssetMutation,
  useGetChildrenAssetsQuery,
  useGetParentAssetsQuery,
  useRemoveChildrenFromParentAssetMutation,
  useRemoveParentsFromChildAssetMutation,
} from "../store/slices/mapsSlice";
import AssetList from "./AssetList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { loadAssetOptionsPaginated } from "../proxyAPI";
import { formatAssetKind } from "./AssetKind";
import MyPagination from "../Common/MyPagination";

const RELATION_ASSETS_PER_PAGE_OPTIONS = [12, 24, 48];

function PaginatedAssetRelationList({
  assets,
  actions,
}: {
  assets: AssetType[];
  actions: (asset: AssetType) => React.ReactNode;
}) {
  const [pagination, setPagination] = useState({ page: 0, perPage: 12 });
  const paginatedAssets = assets.slice(
    pagination.page * pagination.perPage,
    (pagination.page + 1) * pagination.perPage
  );

  return (
    <>
      <AssetList showTable assets={paginatedAssets} actions={actions} />
      <MyPagination
        key={assets.length}
        total={assets.length}
        perPageOptions={RELATION_ASSETS_PER_PAGE_OPTIONS}
        onPaginate={(page: number, perPage: number) =>
          setPagination({ page, perPage })
        }
      />
    </>
  );
}

export default function AssetRelations({
  selectedAsset,
  relationType,
}: {
  relationType?: string;
  selectedAsset: Partial<AssetType>;
}) {
  const { data: { assets: childrenAssets } = { assets: [] } } =
    useGetChildrenAssetsQuery(selectedAsset?.Uuid ?? "", {
      skip: !selectedAsset?.Uuid,
    });
  const { data: { assets: parentAssets } = { assets: [] } } =
    useGetParentAssetsQuery(selectedAsset?.Uuid ?? "", {
      skip: !selectedAsset?.Uuid,
    });

  const [newChildAssets, setNewChildAssets] = useState<string[]>([]);
  const [newParentAssets, setNewParentAssets] = useState<string[]>([]);

  const [setAsParentAsset] = useAddChildrenToParentAssetMutation();
  const [setAsChildAsset] = useAddParentsToChildAssetMutation();
  const [removeChildrenFromParentAsset] =
    useRemoveChildrenFromParentAssetMutation();
  const [removeParentsFromChildAsset] =
    useRemoveParentsFromChildAssetMutation();

  const excludedAssetUuids = [
    selectedAsset?.Uuid,
    ...(childrenAssets || []).map((asset) => asset.Uuid),
    ...(parentAssets || []).map((asset) => asset.Uuid),
  ].filter(Boolean);
  const excludedAssetUuidsKey = excludedAssetUuids.join(",");

  const loadDependantAssetOptions = async (
    search: string,
    prevOptions: any[]
  ) => {
    return loadAssetOptionsPaginated(
      search,
      prevOptions,
      excludedAssetUuids as string[]
    );
  };

  const formatAssetOptionLabel = (o: any) => {
    if (!o.data) {
      return o.label;
    }

    return (
      <div>
      <span className="fw-bold">Label:</span> {o.data.Label}
        <div>
          <span className="fw-bold">Kind:</span> {formatAssetKind(o.data.Kind)}
        </div>
        <div>
          <span className="fw-bold">Cust Ref:</span> {o.data.Reference}
        </div>
        <div>
          <span className="fw-bold">Desc:</span> {o.data.Description}
        </div>
      </div>
    );
  };

  return (
    <div className="p-3">
      <div>
        <div>
      <span className="fw-bold">Asset label:</span>
          <span className="text-secondary ms-2">{selectedAsset.Label}</span>
        </div>
        <div>
          <span className="fw-bold">Asset description:</span>
          <span className="text-secondary ms-2">
            {selectedAsset.Description}
          </span>
        </div>
        <div className="text-secondary">
          Connect multiple assets to this one or create Asset dependencies
        </div>
      </div>
      {relationType !== "connected" && (
        <div className="bg-purple-gradient-darker-reverse p-3 rounded-4 my-3">
          <div className="mb-5 mt-2">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
              <div className="">
                <div className="fs-5 fw-bold"> Dependant Assets</div>
                <div className="text-secondary text-nowrap">
                  Create dependent assets under the principal asset
                </div>
              </div>
              <div className="d-flex gap-1">
                <Input
                  key={excludedAssetUuidsKey}
                  // label="Add child assets"
                  placeholder="Select dependant assets"
                  value={newChildAssets}
                  onChange={setNewChildAssets}
                  loadOptions={loadDependantAssetOptions}
                  inputType="select"
                  isMulti
                  formatOptionLabel={formatAssetOptionLabel}
                />
                <button
                  className="btn btn-colored text-nowrap"
                  disabled={newChildAssets.length === 0}
                  onClick={async () => {
                    const res = await setAsParentAsset({
                      assetUuid: selectedAsset?.Uuid || "",
                      children: newChildAssets,
                    });
                    if ("data" in res) {
                      setNewChildAssets([]);
                    }
                  }}
                >
                  Add dependant assets
                </button>
              </div>
            </div>
            {childrenAssets?.length > 0 ? (
              <PaginatedAssetRelationList
                assets={childrenAssets}
                actions={(a: AssetType) => (
                  <button
                    className="btn text-navy"
                    onClick={async () => {
                      await removeChildrenFromParentAsset({
                        assetUuid: selectedAsset?.Uuid || "",
                        children: [a.Uuid].filter(
                          (uuid): uuid is string => !!uuid
                        ),
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} size="lg" />
                  </button>
                )}
              />
            ) : (
              <p className="text-muted">No dependant assets found</p>
            )}
          </div>
        </div>
      )}
      <hr className="border-secondary-subtle" />
      {relationType !== "dependant" && (
        <div className="bg-purple-gradient-darker-reverse p-3 rounded-4 my-3">
          <div className="mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
              <div>
                <div className="fw-bold fs-5"> Connected Assets</div>
                <div className="text-secondary">
                  Select one or more assets that you want to group together
                </div>
              </div>
              <div className="d-flex gap-1">
                <Input
                  // label="Parent assets"
                  key={excludedAssetUuidsKey}
                  placeholder="Select connected assets"
                  value={newParentAssets}
                  onChange={setNewParentAssets}
                  loadOptions={loadDependantAssetOptions}
                  inputType="select"
                  isMulti
                  formatOptionLabel={formatAssetOptionLabel}
                />
                <button
                  className="btn btn-colored text-nowrap"
                  disabled={newParentAssets.length === 0}
                  onClick={async () => {
                    const res = await setAsChildAsset({
                      assetUuid: selectedAsset?.Uuid || "",
                      parents: newParentAssets,
                    });
                    if ("data" in res) {
                      setNewParentAssets([]);
                    }
                  }}
                >
                  Add connected assets
                </button>
              </div>
            </div>
            {parentAssets?.length > 0 ? (
              <PaginatedAssetRelationList
                assets={parentAssets}
                actions={(a: AssetType) => (
                  <button
                    className="btn text-navy"
                    onClick={async () => {
                      await removeParentsFromChildAsset({
                        assetUuid: selectedAsset?.Uuid || "",
                        parents: [a.Uuid].filter(
                          (uuid): uuid is string => !!uuid
                        ),
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} size="lg" />
                  </button>
                )}
              />
            ) : (
              <p className="text-muted">No connected assets found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
